export const WEEK_DAYS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

export const COLOR_OPTIONS = [
  { label: "Bleu", value: "bg-blue-400" },
  { label: "Vert", value: "bg-green-400" },
  { label: "Jaune", value: "bg-yellow-400" },
  { label: "Rouge", value: "bg-red-400" },
  { label: "Indigo", value: "bg-indigo-400" },
  { label: "Violet", value: "bg-purple-400" },
  { label: "Rose", value: "bg-pink-400" },
  { label: "Orange", value: "bg-orange-400" },
  { label: "Cyan", value: "bg-teal-400" },
  { label: "Marron", value: "bg-amber-600" },
  { label: "Turquoise", value: "bg-cyan-500" },
  { label: "Gris", value: "bg-gray-500" },
  { label: "Lime", value: "bg-lime-400" },
  { label: "Fuchsia", value: "bg-fuchsia-500" },
];

export const DEFAULT_DISCIPLINES = [
  "Judo",
  "Jujitsu",
  "Taïso",
  "Ne Waza",
  "Self-défense",
  "Krav Maga",
  "MMA",
  "Baby Judo",
  "Éveil",
  "Renforcement musculaire",
  "Karaté",
  "Taekwondo",
  "Aïkido",
  "Capoeira",
  "Boxe",
];

export const QUILL_MODULES = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};

export const QUILL_FORMATS = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

export const EMPTY_GROUP_FORM = {
  id: "",
  nom: "",
  discipline_groupe: "",
  horaire1: "",
  horaire2: "",
  horaire3: "",
  horaire4: "",
  horaire5: "",
  horaire6: "",
  annees_naissance: "",
  tarif_licence: "",
  tarif_adhesion: "",
  tarif_cotisation: "",
  info_groupe: "",
  ordre_groupe: "",
  quota_groupe: "",
  is_active: true,
  inscription_active: true,
  enseignant1: "",
  enseignant2: "",
  enseignant3: "",
  enseignant4: "",
  enseignant5: "",
  enseignant6: "",
  color_groupe: COLOR_OPTIONS[0].value,
  dojo_id: "",
};

export function toBool(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

export function toNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function sortGroupsByOrder(groups) {
  return safeArray(groups)
    .slice()
    .sort((a, b) => {
      const dojoA = toNumber(a?.dojo_id, 0);
      const dojoB = toNumber(b?.dojo_id, 0);

      if (dojoA !== dojoB) return dojoA - dojoB;

      const orderA = toNumber(a?.ordre_groupe, 0);
      const orderB = toNumber(b?.ordre_groupe, 0);

      if (orderA !== orderB) return orderA - orderB;

      return String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr");
    });
}

export function getDojosById(dojos) {
  return safeArray(dojos).reduce((acc, dojo) => {
    acc[String(dojo.id)] = dojo;
    return acc;
  }, {});
}

export function getGroupTeachers(group) {
  return [
    group?.enseignant1,
    group?.enseignant2,
    group?.enseignant3,
    group?.enseignant4,
    group?.enseignant5,
    group?.enseignant6,
  ].filter((value) => String(value || "").trim() !== "");
}

export function getGroupSchedules(group) {
  return [
    group?.horaire1,
    group?.horaire2,
    group?.horaire3,
    group?.horaire4,
    group?.horaire5,
    group?.horaire6,
  ].filter((value) => String(value || "").trim() !== "");
}

export function parseHoraireValue(value = "") {
  const cleaned = String(value || "").trim();
  if (!cleaned) return null;

  const match = cleaned.match(/^(.+?)\s+(\d{2}:\d{2})-(\d{2}:\d{2})$/);

  if (!match) return null;

  const [, jourRaw, debut, fin] = match;

  const jour =
    WEEK_DAYS.find((item) => item.toLowerCase() === jourRaw.toLowerCase()) ||
    jourRaw;

  return { jour, debut, fin };
}

export function getGroupAgeMode(anneesNaissance = "") {
  const raw = String(anneesNaissance || "").trim();

  if (!raw) {
    return {
      modeAge: "annees",
      ageMin: "",
      anneesSelectionnees: [],
    };
  }

  const ageMatch = raw.match(/^\+\s*(\d+)\s*ans$/i);

  if (ageMatch) {
    return {
      modeAge: "ageMin",
      ageMin: ageMatch[1],
      anneesSelectionnees: [],
    };
  }

  return {
    modeAge: "annees",
    ageMin: "",
    anneesSelectionnees: raw
      .split("-")
      .map((year) => parseInt(year, 10))
      .filter((year) => Number.isFinite(year)),
  };
}

export function buildAnneesNaissance({ modeAge, ageMin, anneesSelectionnees }) {
  if (modeAge === "ageMin" && ageMin) {
    return `+${ageMin} ans`;
  }

  if (modeAge === "annees") {
    return safeArray(anneesSelectionnees)
      .slice()
      .sort((a, b) => b - a)
      .join("-");
  }

  return "";
}

export function formatEuroValue(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) return "—";

  return number.toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function getQuotaMeta(group) {
  const members = toNumber(group?.nombreAdherents, 0);
  const quota = toNumber(group?.quota_groupe, 0);
  const hasQuota = quota > 0;
  const percent = hasQuota
    ? Math.min(100, Math.round((members / quota) * 100))
    : 0;
  const isFull = hasQuota && members >= quota;
  const isAlmostFull = hasQuota && members < quota && percent >= 80;

  return {
    members,
    quota,
    hasQuota,
    percent,
    isFull,
    isAlmostFull,
    remaining: hasQuota ? Math.max(0, quota - members) : null,
  };
}

export function getQuotaBadgeMeta(group, isQuotaActive) {
  const quota = getQuotaMeta(group);

  if (!quota.hasQuota) {
    return {
      label: "Sans quota",
      className:
        "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
      tone: "neutral",
    };
  }

  if (quota.isFull && isQuotaActive) {
    return {
      label: "Complet bloqué",
      className:
        "bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
      tone: "danger",
    };
  }

  if (quota.isFull) {
    return {
      label: "Complet",
      className:
        "bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      tone: "warn",
    };
  }

  if (quota.isAlmostFull) {
    return {
      label: "Presque complet",
      className:
        "bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      tone: "warn",
    };
  }

  return {
    label: "Places disponibles",
    className:
      "bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
    tone: "ok",
  };
}

export function filterGroups(groups, filters) {
  const query = normalizeText(filters?.query || "");
  const dojoId = String(filters?.dojoId || "");
  const status = String(filters?.status || "");
  const inscriptionState = String(filters?.inscriptionState || "");
  const quotaState = String(filters?.quotaState || "");

  return sortGroupsByOrder(groups).filter((group) => {
    const matchQuery =
      !query ||
      normalizeText(
        `${group?.nom || ""} ${group?.discipline_groupe || ""} ${
          group?.annees_naissance || ""
        } ${getGroupTeachers(group).join(" ")} ${getGroupSchedules(group).join(" ")}`,
      ).includes(query);

    const matchDojo = !dojoId || String(group?.dojo_id) === dojoId;

    const matchStatus =
      !status ||
      status === "all" ||
      (status === "active" && toBool(group?.is_active)) ||
      (status === "inactive" && !toBool(group?.is_active));

    const matchInscription =
      !inscriptionState ||
      inscriptionState === "all" ||
      (inscriptionState === "open" && toBool(group?.inscription_active)) ||
      (inscriptionState === "closed" && !toBool(group?.inscription_active));

    const quota = getQuotaMeta(group);

    const matchQuota =
      !quotaState ||
      quotaState === "all" ||
      (quotaState === "withQuota" && quota.hasQuota) ||
      (quotaState === "withoutQuota" && !quota.hasQuota) ||
      (quotaState === "full" && quota.isFull) ||
      (quotaState === "available" && quota.hasQuota && !quota.isFull);

    return (
      matchQuery && matchDojo && matchStatus && matchInscription && matchQuota
    );
  });
}

export function calculateGroupsStats(groups) {
  const rows = safeArray(groups);

  let active = 0;
  let inactive = 0;
  let members = 0;
  let withQuota = 0;
  let full = 0;
  let inscriptionOpen = 0;
  let inscriptionClosed = 0;
  const disciplines = new Set();

  for (const group of rows) {
    if (toBool(group?.inscription_active)) inscriptionOpen += 1;
    else inscriptionClosed += 1;

    members += toNumber(group?.nombreAdherents, 0);

    const quota = getQuotaMeta(group);

    if (quota.hasQuota) withQuota += 1;
    if (quota.isFull) full += 1;

    if (group?.discipline_groupe) disciplines.add(group.discipline_groupe);
  }

  return {
    total: rows.length,
    active,
    inactive,
    inscriptionOpen,
    inscriptionClosed,
    members,
    withQuota,
    full,
    disciplinesCount: disciplines.size,
  };
}

export function getNextGroupOrder(groups, dojoId = "") {
  const rows = safeArray(groups).filter((group) => {
    if (!dojoId) return true;
    return String(group?.dojo_id) === String(dojoId);
  });

  return (
    rows.reduce((max, group) => {
      return Math.max(max, toNumber(group?.ordre_groupe, 0));
    }, 0) + 1
  );
}

export function getDuplicateGroupData(group) {
  const baseName = String(group?.nom || "")
    .replace(/\s*\(copie\)$/i, "")
    .trim();

  return {
    ...group,
    id: undefined,
    nom: `${baseName || "Groupe"} (copie)`,
    nombreAdherents: 0,
  };
}

export function mapGroupToFormState(group, options = {}) {
  const nextOrder = options.nextOrder || 1;
  const defaultDojoId = options.defaultDojoId || "";

  if (!group) {
    return {
      formData: {
        ...EMPTY_GROUP_FORM,
        dojo_id: defaultDojoId,
        ordre_groupe: nextOrder,
      },
      horaires: [{ jour: "", debut: "", fin: "" }],
      enseignantsList: [""],
      infoGroupe: "",
      modeAge: "annees",
      ageMin: "",
      anneesSelectionnees: [],
    };
  }

  const horaires = [
    group.horaire1,
    group.horaire2,
    group.horaire3,
    group.horaire4,
    group.horaire5,
    group.horaire6,
  ]
    .map(parseHoraireValue)
    .filter(Boolean);

  const enseignantsList = getGroupTeachers(group);
  const age = getGroupAgeMode(group.annees_naissance);

  return {
    formData: {
      ...EMPTY_GROUP_FORM,
      id: group.id || "",
      nom: group.nom || "",
      discipline_groupe: group.discipline_groupe || "",
      tarif_licence: group.tarif_licence ?? "",
      tarif_adhesion: group.tarif_adhesion ?? "",
      tarif_cotisation: group.tarif_cotisation ?? "",
      ordre_groupe: group.ordre_groupe ?? nextOrder,
      quota_groupe: group.quota_groupe ?? "",
      is_active: group.is_active === undefined ? true : toBool(group.is_active),
      inscription_active:
        group.inscription_active === undefined
          ? true
          : toBool(group.inscription_active),
      color_groupe: group.color_groupe || COLOR_OPTIONS[0].value,
      dojo_id: group.dojo_id != null ? String(group.dojo_id) : defaultDojoId,
    },
    horaires: horaires.length ? horaires : [{ jour: "", debut: "", fin: "" }],
    enseignantsList: enseignantsList.length ? enseignantsList : [""],
    infoGroupe: group.info_groupe || "",
    modeAge: age.modeAge,
    ageMin: age.ageMin,
    anneesSelectionnees: age.anneesSelectionnees,
  };
}

export function buildGroupPayload({
  formData,
  horaires,
  enseignantsList,
  infoGroupe,
  modeAge,
  ageMin,
  anneesSelectionnees,
}) {
  const horairesFormatted = safeArray(horaires)
    .filter((item) => item.jour && item.debut && item.fin)
    .map((item) => `${item.jour} ${item.debut}-${item.fin}`);

  const teachers = safeArray(enseignantsList).filter(
    (teacher) => String(teacher || "").trim() !== "",
  );

  return {
    ...formData,
    nom: String(formData.nom || "").trim(),
    discipline_groupe: String(formData.discipline_groupe || "").trim(),
    dojo_id: formData.dojo_id ? Number(formData.dojo_id) : "",
    tarif_licence: formData.tarif_licence,
    tarif_adhesion: formData.tarif_adhesion,
    tarif_cotisation: formData.tarif_cotisation,
    ordre_groupe:
      formData.ordre_groupe === "" ? 0 : Number(formData.ordre_groupe),
    quota_groupe:
      formData.quota_groupe === "" ? 0 : Number(formData.quota_groupe),
    is_active: toBool(formData.is_active),
    inscription_active: toBool(formData.inscription_active),
    color_groupe: formData.color_groupe || COLOR_OPTIONS[0].value,
    info_groupe: infoGroupe || "",

    enseignant1: teachers[0] || "",
    enseignant2: teachers[1] || "",
    enseignant3: teachers[2] || "",
    enseignant4: teachers[3] || "",
    enseignant5: teachers[4] || "",
    enseignant6: teachers[5] || "",

    horaire1: horairesFormatted[0] || "",
    horaire2: horairesFormatted[1] || "",
    horaire3: horairesFormatted[2] || "",
    horaire4: horairesFormatted[3] || "",
    horaire5: horairesFormatted[4] || "",
    horaire6: horairesFormatted[5] || "",

    annees_naissance: buildAnneesNaissance({
      modeAge,
      ageMin,
      anneesSelectionnees,
    }),
  };
}

export function validateGroupPayload(payload) {
  if (!payload.nom) return "Le nom du groupe est requis.";
  if (!payload.dojo_id) return "Veuillez sélectionner un dojo.";
  if (!Number.isFinite(Number(payload.ordre_groupe))) {
    return "L’ordre d’affichage doit être un nombre.";
  }
  if (!Number.isFinite(Number(payload.quota_groupe))) {
    return "Le quota doit être un nombre.";
  }

  return "";
}
